import { isRulebookEnabled } from '../utils/rulebookUtils';
/**
 * Skills Service
 * Handles skill management including Basic/Advanced skill rules.
 *
 * All methods use string-based IDs exclusively (no numeric IDs).
 * Skills are identified by their kebab-case string `id` field.
 *
 * Requirements: 1.1, 1.5, 3.1, 5.1
 */

import skillsData from '../data/skills.json';
import {
  Skill,
  SkillTraining,
  SkillCategory,
  SkillPurchase,
  Characteristics,
  Talent,
  ConditionalModifier
} from '../types/character';
import { TalentsService } from './TalentsService';
import { createSkillPurchase } from '../utils/purchaseRecordHelpers';

/**
 * Result of calculating a skill's total value from character data.
 * Contains all components that contribute to the final skill value.
 * All calculations use only character data - no external lookups required.
 *
 * Requirements: 2.1, 2.2, 3.4, 4.3, 9.2
 */
export interface SkillCalculation {
  /** The string ID of the skill being calculated */
  skillId: string;

  /** Base value from the skill's governing characteristic */
  baseValue: number;

  /**
   * Training bonus from purchase history.
   * This is the MAX of purchase bonuses (not sum), since +20 replaces +10.
   * Values: 0 (untrained), 10 (+10), 20 (+20), 30 (+30)
   */
  trainingBonus: number;

  /**
   * Bonus from "Talented" talent purchases.
   * Max one +10 bonus per skill - multiple "Talented" purchases for the same skill
   * do not stack.
   */
  talentedBonus: number;

  /**
   * Conditional modifiers that may apply situationally.
   * These are NOT included in finalValue - they apply based on circumstances.
   */
  conditionalModifiers: ConditionalModifier[];

  /**
   * Final calculated skill value.
   * = baseValue + trainingBonus + talentedBonus
   * (conditional modifiers are tracked separately as they're situational)
   */
  finalValue: number;
}

/**
 * Represents a special use case for a skill.
 * Special uses are alternative or additional ways to use a skill beyond its primary function.
 *
 * Requirements: 1.4
 */
export interface SpecialUse {
  /** Name of the special use (e.g., "Disengage", "Jump & Leap") */
  name: string;

  /** Description of how this special use works */
  description: string;
}

/**
 * Definition of a skill from the skills.json data file.
 * Uses string ID as the primary identifier - no numeric IDs.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.6, 5.1, 5.3
 */
export interface SkillDefinition {
  /**
   * String ID used as the primary identifier.
   * Uses kebab-case format for consistency.
   * @example "acrobatics", "dodge", "common-lore-imperium"
   */
  id: string;

  /** Display name shown in the UI */
  name: string;

  /** The characteristic this skill is based on */
  characteristic: string;

  /** Category for UI organization */
  category: string;

  /** Whether this skill requires specialization */
  isSpecialist: boolean;

  /** Source rulebook */
  rulebook: string;

  /** Skill type: 'Basic' or 'Advanced' */
  type?: 'Basic' | 'Advanced';

  /** Grouping descriptor for organization */
  descriptor?: string;

  /** For specialist skills, the base skill name */
  baseSkill?: string;

  /** For specialist skills, the specific specialization */
  specialization?: string;

  /**
   * Brief one-sentence summary suitable for hover tooltips.
   * Typically under 100 characters.
   * Requirements: 1.1
   */
  shortDescription: string;

  /**
   * Complete description of the skill including what it does and how it works.
   * Requirements: 1.2
   */
  fullDescription: string;

  /**
   * Structured string indicating how long a skill takes to use.
   * Format examples: "action:full", "minute:1", "hour:1d5", "variable", "extended"
   * Requirements: 1.3
   */
  skillUseDuration?: string;

  /**
   * Array of special use cases for the skill.
   * Each special use has a name and description.
   * Requirements: 1.4
   */
  specialUses?: SpecialUse[];
}

export class SkillsService {
  /**
   * Get a skill definition by its string ID.
   *
   * @param id - The string ID of the skill (e.g., "acrobatics", "dodge")
   * @returns The skill definition or undefined if not found
   *
   * Requirements: 1.1, 1.5, 5.1
   */
  static getSkillById(id: string): SkillDefinition | undefined {
    return skillsData.skills.find((s) => s.id === id) as SkillDefinition | undefined;
  }

  /**
   * Get all Basic skills from the data
   */
  private static getBasicSkills(): SkillDefinition[] {
    return skillsData.skills.filter((skill) => skill.type === 'Basic') as SkillDefinition[];
  }

  /**
   * Get all skills from the data
   */
  static getAllSkills(): SkillDefinition[] {
    return skillsData.skills as SkillDefinition[];
  }

  /**
   * Calculate the skill total for a character
   * Implements the Basic/Advanced skill rules:
   * - Basic skills untrained: use half characteristic (rounded down)
   * - Basic skills trained: use full characteristic + training bonus
   * - Advanced skills untrained: cannot be used
   * - Advanced skills trained: use full characteristic + training bonus
   */
  static calculateSkillTotal(
    skill: Skill,
    characteristicValue: number,
    skillType?: 'Basic' | 'Advanced',
    conditionalModifiers: import('../types/character').ConditionalModifier[] = []
  ): number {
    // Determine training bonus
    let trainingBonus = 0;
    switch (skill.training) {
      case SkillTraining.Untrained:
        // Basic skills use half characteristic when untrained
        if (skillType === 'Basic') {
          return Math.floor(characteristicValue / 2);
        }
        // Advanced skills cannot be used untrained
        return 0;
      case SkillTraining.Trained:
        trainingBonus = 0;
        break;
      case SkillTraining.Experienced:
        trainingBonus = 10;
        break;
      case SkillTraining.Adept:
        trainingBonus = 20;
        break;
    }

    // Calculate conditional modifiers
    const modifiersTotal = conditionalModifiers
      .filter((mod) => {
        // Match by exact skill name or specialization
        return (
          mod.target === skill.name || (skill.specialization && mod.target === skill.specialization)
        );
      })
      .reduce((sum, mod) => sum + mod.value, 0);

    return characteristicValue + trainingBonus + modifiersTotal;
  }

  /**
   * Get the skill type from the data
   */
  static getSkillType(skillName: string): 'Basic' | 'Advanced' | undefined {
    // Match by exact name - skills should have proper base names
    const skillDef = skillsData.skills.find((s) => {
      return s.name.toLowerCase() === skillName.toLowerCase();
    });

    return skillDef?.type as 'Basic' | 'Advanced' | undefined;
  }

  /**
   * Initialize all Basic skills for a character
   * Basic skills should always be present on the character sheet.
   * Uses string IDs from skill definitions.
   *
   * Requirements: 1.1, 3.1, 5.1
   */
  static initializeBasicSkills(enabledRulebooks: string[]): Skill[] {
    const basicSkills = this.getBasicSkills();

    return basicSkills
      .filter((skillDef) => isRulebookEnabled(skillDef.rulebook, enabledRulebooks))
      .map((skillDef) => ({
        id: skillDef.id, // Use string ID from skill definition
        name: skillDef.name,
        type: skillDef.type,
        characteristic:
          skillDef.characteristic as keyof import('../types/character').Characteristics,
        category: skillDef.category as SkillCategory,
        training: SkillTraining.Untrained,
        isSpecialist: skillDef.isSpecialist,
        shortDescription: skillDef.shortDescription,
        rulebook: skillDef.rulebook,
        purchaseHistory: [] // Initialize empty purchase history for new skills
      }));
  }

  /**
   * Check if a skill can be used untrained
   */
  static canUseUntrained(skillName: string): boolean {
    const skillType = this.getSkillType(skillName);
    return skillType === 'Basic';
  }

  /**
   * Add a skill purchase to a character's skill list.
   * Creates the skill if it doesn't exist, or updates the existing skill's purchase history.
   *
   * @param characterSkills - The character's current skills array
   * @param skillId - The string ID of the skill (e.g., "dodge", "commerce")
   * @param advancementId - The unique ID of the advancement being purchased
   * @param rank - The career rank when this advancement was purchased
   * @param bonus - The bonus granted by this purchase (10 or 20)
   * @returns Updated skills array with the new purchase recorded
   *
   * Requirements: 1.1, 1.5, 3.1, 5.1
   */
  static addSkillPurchase(
    characterSkills: Skill[],
    skillId: string,
    advancementId: string,
    rank: number,
    bonus: number
  ): Skill[] {
    // Find the skill definition
    const skillDef = this.getSkillById(skillId);
    if (!skillDef) {
      console.warn(`Skill not found: ${skillId}`);
      return characterSkills;
    }

    // Create the purchase record
    const purchase = createSkillPurchase(advancementId, rank, bonus);

    // Find existing skill in character's skills
    const existingSkillIndex = characterSkills.findIndex(
      (s) => s.id === skillId || s.name.toLowerCase() === skillDef.name.toLowerCase()
    );

    if (existingSkillIndex >= 0) {
      // Update existing skill
      const updatedSkills = [...characterSkills];
      const existingSkill = updatedSkills[existingSkillIndex];

      // Ensure purchaseHistory exists
      const purchaseHistory = existingSkill.purchaseHistory || [];

      updatedSkills[existingSkillIndex] = {
        ...existingSkill,
        id: skillId, // Ensure we're using the string ID
        purchaseHistory: [...purchaseHistory, purchase],
        training: this.calculateTrainingLevel([...purchaseHistory, purchase])
      };

      return updatedSkills;
    } else {
      // Create new skill with the purchase
      const newSkill: Skill = {
        id: skillId,
        name: skillDef.name,
        type: skillDef.type,
        characteristic: skillDef.characteristic as keyof Characteristics,
        category: skillDef.category as SkillCategory,
        training: this.calculateTrainingLevel([purchase]),
        isSpecialist: skillDef.isSpecialist,
        specialization: skillDef.specialization,
        shortDescription: skillDef.shortDescription,
        rulebook: skillDef.rulebook,
        baseSkill: skillDef.baseSkill,
        purchaseHistory: [purchase]
      };

      return [...characterSkills, newSkill];
    }
  }

  /**
   * Calculate the training level based on purchase history.
   * Training bonus is the MAX of purchase bonuses (not sum), since +20 replaces +10.
   *
   * @param purchaseHistory - Array of skill purchases
   * @returns The appropriate SkillTraining level
   *
   * Requirements: 3.4
   */
  private static calculateTrainingLevel(purchaseHistory: SkillPurchase[]): SkillTraining {
    if (!purchaseHistory || purchaseHistory.length === 0) {
      return SkillTraining.Untrained;
    }

    // Get the maximum bonus from purchase history
    const maxBonus = Math.max(...purchaseHistory.map((p) => p.bonus));

    // Map bonus to training level
    // The bonus field directly represents the skill modifier from advancements:
    // - bonus: 0 (or no bonus) = Trained (gives +0 modifier)
    // - bonus: 10 = Experienced (gives +10 modifier)
    // - bonus: 20 = Adept (gives +20 modifier)
    if (maxBonus >= 20) {
      return SkillTraining.Adept;
    } else if (maxBonus >= 10) {
      return SkillTraining.Experienced;
    } else {
      return SkillTraining.Trained;
    }
  }

  /**
   * Get the training bonus value from purchase history.
   * Returns the MAX bonus (not sum), since higher training replaces lower.
   *
   * The bonus field in purchase history directly represents the skill modifier:
   * - No purchases = Untrained (-20 penalty, but we return 0 here)
   * - bonus: 10 = +10 modifier (Experienced)
   * - bonus: 20 = +20 modifier (Adept)
   * - bonus: 30 = +30 modifier (Master)
   *
   * Note: "Trained" level (no bonus) is represented by having the skill but
   * with no purchases that have a bonus value, or bonus: 0.
   *
   * @param purchaseHistory - Array of skill purchases
   * @returns The training bonus value (0, 10, 20, or 30)
   *
   * Requirements: 3.4
   */
  static getTrainingBonus(purchaseHistory: SkillPurchase[]): number {
    if (!purchaseHistory || purchaseHistory.length === 0) {
      return 0;
    }
    return Math.max(...purchaseHistory.map((p) => p.bonus));
  }

  /**
   * Remove a skill purchase from a character's skill.
   * Updates the training level based on remaining purchases.
   *
   * @param characterSkills - The character's current skills array
   * @param skillId - The string ID of the skill
   * @param advancementId - The advancement ID to remove
   * @returns Updated skills array with the purchase removed
   *
   * Requirements: 3.1
   */
  static removeSkillPurchase(
    characterSkills: Skill[],
    skillId: string,
    advancementId: string
  ): Skill[] {
    const skillIndex = characterSkills.findIndex((s) => s.id === skillId);
    if (skillIndex < 0) {
      return characterSkills;
    }

    const skill = characterSkills[skillIndex];
    const updatedHistory = (skill.purchaseHistory || []).filter(
      (p) => p.advancementId !== advancementId
    );

    const updatedSkills = [...characterSkills];
    updatedSkills[skillIndex] = {
      ...skill,
      purchaseHistory: updatedHistory,
      training: this.calculateTrainingLevel(updatedHistory)
    };

    return updatedSkills;
  }

  /**
   * Check if a skill has a specific advancement purchase.
   *
   * @param skill - The skill to check
   * @param advancementId - The advancement ID to look for
   * @returns True if the skill has this advancement purchase
   */
  static hasAdvancementPurchase(skill: Skill, advancementId: string): boolean {
    return (skill.purchaseHistory || []).some((p) => p.advancementId === advancementId);
  }

  /**
   * Calculate the complete skill value from character data only.
   * This is the primary method for determining a skill's total value.
   *
   * Calculation:
   * - Base value from the skill's governing characteristic
   * - Training bonus: MAX of purchase history bonuses (not sum, since +20 replaces +10)
   * - Talented bonus: +10 if the skill has "Talented" talent applied (max one per skill)
   * - Final value = base + training + talented
   *
   * Conditional modifiers are tracked separately as they apply situationally.
   *
   * This method uses ONLY character data - no external lookups required.
   *
   * @param skills - The character's skills array
   * @param talents - The character's talents array
   * @param characteristics - The character's characteristics
   * @param skillId - The string ID of the skill to calculate
   * @param conditionalModifiers - Optional array of conditional modifiers from character
   * @returns SkillCalculation with all components and final value
   *
   * Requirements: 2.1, 2.2, 3.4, 4.3, 9.2
   */
  static calculateSkillValue(
    skills: Skill[],
    talents: Talent[],
    characteristics: Characteristics,
    skillId: string,
    conditionalModifiers: ConditionalModifier[] = []
  ): SkillCalculation {
    // Find the skill in character data
    const skill = skills.find((s) => s.id === skillId);

    // If skill not found, return zero calculation
    if (!skill) {
      return {
        skillId,
        baseValue: 0,
        trainingBonus: 0,
        talentedBonus: 0,
        conditionalModifiers: [],
        finalValue: 0
      };
    }

    // 1. Base value from characteristic
    const baseValue = characteristics[skill.characteristic] || 0;

    // 2. Training bonus from purchase history - take HIGHEST (not sum)
    // Since +20 replaces +10, we use the maximum bonus value
    const trainingBonus = this.getTrainingBonus(skill.purchaseHistory || []);

    // 3. Talented bonus - check if this skill has "Talented" applied
    // A skill can only have one "Talented" bonus (max +10)
    let talentedBonus = TalentsService.getTalentedBonusForSkill(talents, skillId);

    // Also check conditional modifiers for Talented applied during character creation
    // (These have condition: 'always' and source starting with 'Talented')
    if (talentedBonus === 0) {
      const talentedMod = conditionalModifiers.find(
        (mod) =>
          mod.source.startsWith('Talented') &&
          mod.condition === 'always' &&
          (mod.target === skill.name ||
            mod.target === skill.id ||
            (skill.specialization && mod.target === skill.specialization))
      );
      if (talentedMod) talentedBonus = talentedMod.value;
    }

    // 4. Get conditional modifiers that apply to this skill
    // These are tracked separately as they apply situationally
    const applicableModifiers = conditionalModifiers.filter((mod) => {
      // Match by exact skill name or specialization
      return (
        mod.target === skill.name ||
        mod.target === skill.id ||
        (skill.specialization && mod.target === skill.specialization)
      );
    });

    // 5. Calculate final value (without conditional modifiers - those are situational)
    const finalValue = baseValue + trainingBonus + talentedBonus;

    return {
      skillId,
      baseValue,
      trainingBonus,
      talentedBonus,
      conditionalModifiers: applicableModifiers,
      finalValue
    };
  }
}

/**
 * Format a skill use duration string into a human-readable display string.
 *
 * Supported formats:
 * - `action:free` -> "Free Action"
 * - `action:half` -> "Half Action"
 * - `action:full` -> "Full Action"
 * - `action:reaction` -> "Reaction"
 * - `minute:N` -> "N minute" or "N minutes" (singular/plural)
 * - `hour:N` -> "N hour" or "N hours" (handles dice expressions like "1d5")
 * - `variable` -> "Variable"
 * - `extended` -> "Extended Test"
 *
 * For unrecognized formats, returns the original string unchanged and logs
 * a warning in development mode.
 *
 * @param duration - The duration format string to parse
 * @returns Human-readable duration string
 *
 * Requirements: 2.1-2.8, 7.1-7.10
 */
export function formatSkillDuration(duration: string): string {
  if (!duration) {
    return '';
  }

  // Handle special formats first
  if (duration === 'variable') {
    return 'Variable';
  }

  if (duration === 'extended') {
    return 'Extended Test';
  }

  // Handle action formats
  if (duration.startsWith('action:')) {
    const actionType = duration.substring(7); // Remove 'action:' prefix
    switch (actionType) {
      case 'free':
        return 'Free Action';
      case 'half':
        return 'Half Action';
      case 'full':
        return 'Full Action';
      case 'reaction':
        return 'Reaction';
      default:
        // Unknown action type - fall through to unrecognized format handling
        break;
    }
  }

  // Handle minute formats: minute:N
  if (duration.startsWith('minute:')) {
    const value = duration.substring(7); // Remove 'minute:' prefix
    const numValue = parseInt(value, 10);

    // Check if it's a valid number
    if (!isNaN(numValue)) {
      return numValue === 1 ? '1 minute' : `${value} minutes`;
    }

    // Non-numeric value (e.g., dice expression) - use plural
    return `${value} minutes`;
  }

  // Handle hour formats: hour:N
  if (duration.startsWith('hour:')) {
    const value = duration.substring(5); // Remove 'hour:' prefix
    const numValue = parseInt(value, 10);

    // Check if it's exactly "1" for singular
    if (value === '1') {
      return '1 hour';
    }

    // Check if it's a valid number > 1
    if (!isNaN(numValue) && numValue > 1) {
      return `${value} hours`;
    }

    // Non-numeric value (e.g., dice expression like "1d5") - use plural
    return `${value} hours`;
  }

  // Unrecognized format - log warning in development and return original
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[SkillsService] Unrecognized skill duration format: "${duration}"`);
  }

  return duration;
}
